import React, { useRef, useState, useEffect } from 'react';
import AppAppBar from './views/AppAppBar';
import AppFooter from './views/AppFooter';
import FeaturesHero from './views/FeaturesHero';
import Box from '@mui/material/Box';
import { useTrail, animated } from '@react-spring/web';
import Typography from './components/Typography';
import en from '../../data/en.json';
import withRoot from '../../withRoot';
import FeatureEssayCraft from './features/EssayCraft';
import FeatureInkBowlPublishing from './features/Publishing';
import FeatureInkLink from './features/InkLink';
import FeatureVerseCanvas from './features/VerseCanvas';

const focusAreas = [
	{
		"tag": "essaycraft",
    "component": <FeatureEssayCraft />
	},
	{
		"tag": "inkbowl-publishing",
    "component": <FeatureInkBowlPublishing />
	},
	{
		"tag": "inklink",
    "component": <FeatureInkLink />
	},
	{
		"tag": "versecanvas",
    "component": <FeatureVerseCanvas />
	},
]

const Features: React.FC = () => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]); // Array of refs for each section
  const [visibleSections, setVisibleSections] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const id = hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.5 } // Adjust visibility threshold as needed
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <React.Fragment>
      <AppAppBar />
      <FeaturesHero />
			<Box
				sx={{
					maxWidth: { xs: "100%", md: "50vw" }, // Mobile: full width, Desktop: 50vw
					textAlign: "center",
					m: "0 auto",
					padding: { xs: "10px 20px", md: "20px" }, // Horizontal padding on mobile to prevent overflow
          mt: { xs: 4, md: 8 }, // Consistent margin with other pages
          mb: { xs: 4, md: 8 }
				}}
			>
				<Typography 
					variant="h5" 
					sx={{ 
						fontWeight: "bold", 
						fontSize: { xs: "1.2rem", md: "1.5rem" }, // Responsive font size
						lineHeight: { xs: 1.3, md: 1.4 }
					}}
				>
					{en.marketing.featureContent.title}
				</Typography>
				<Typography 
					variant="body1" 
					sx={{ 
						mt: 2, 
						fontSize: { xs: "1rem", md: "1.2rem" }, // Responsive font size
						lineHeight: { xs: 1.5, md: 2 } // Responsive line height
					}}
				>
          {en.marketing.featureContent.description}
				</Typography>
			</Box>

      <Box sx={{ 
        p: { xs: 2, sm: 4, md: 10 }, // Responsive padding
        pt: { xs: 8, md: 20 }, // Responsive top padding
        backgroundColor: '#EDEAE9' 
      }}>
        {focusAreas.map((area, index) => {
          const isVisible = visibleSections[area.tag];
          const trail = useTrail(focusAreas.length, {
            opacity: isVisible ? 1 : 0,
            x: isVisible ? 0 : 20,
            from: { opacity: 0, x: 20, height: 0 },
            config: { mass: 5, tension: 2000, friction: 200 },
          });

          return (
            <Box
              key={index}
              id={area.tag} // Anchor point
              ref={(el) => {
                sectionRefs.current[index] = el as HTMLDivElement | null;
              }} // Assign ref
              sx={{
                mb: { xs: 2, md: 4 },
                minHeight: { xs: '80vh', md: '120vh' },
                px: { xs: 1, md: 2 },
                py: { xs: 2, md: 3 },
              }}
            >
              <animated.div
                style={{
                  ...trail[index],
                  height: '100%',
                  width: '100%',
                }}
              >
                {area.component}
              </animated.div>
            </Box>
          );
        })}
      </Box>
      <AppFooter />
    </React.Fragment>
  );
};

export default withRoot(Features);
